import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Chip,
  TextField,
  InputAdornment,
  IconButton,
  Tooltip,
  Paper,
  Collapse,
  Alert,
  Snackbar,
} from '@mui/material';
import {
  TrendingUp,
  TrendingDown,
  Search,
  Bookmark,
  BookmarkBorder,
  ShowChart,
  Assessment,
} from '@mui/icons-material';
import { useAppContext } from '../context/AppContext';
import { Stock } from '../types';
import { WatchlistService } from '../services/watchlistService';
import { useStockData } from '../context/StockDataContext';
import GroupSelectorDialog from './common/GroupSelectorDialog';
import PageHeader from './common/PageHeader';
import DividendDisplay from './DividendDisplay';
import { StockNameManager } from '../services/stockNameManager';
import { staticStockDataService } from '../services/staticStockDataService';
import { StaticStockInfo } from '../services/staticStockDataService';
import {
  getTrendChipProps,
  getTrendDirection,
  getTrendTextColor,
} from '../theme/taiwanTrendColors';

const StockList: React.FC = () => {
  const { allStocks, totalStocksCount, stocksLimit, searchStocks } = useStockData();
  const { state } = useAppContext();
  const [searchTerm, setSearchTerm] = useState('');
  const [stockMarketInfo, setStockMarketInfo] = useState<Map<string, StaticStockInfo>>(new Map());

  useEffect(() => {
    const loadMarketInfo = async () => {
      // UI-05:平行抓取(取代序列 await 的 N+1,allStocks 可達百檔以上)
      const results = await Promise.allSettled(
        allStocks.map((stock) => staticStockDataService.getStockInfo(stock.symbol))
      );
      const marketMap = new Map<string, StaticStockInfo>();
      results.forEach((result) => {
        if (result.status === 'fulfilled' && result.value) {
          marketMap.set(result.value.symbol, result.value);
        }
      });
      setStockMarketInfo(marketMap);
    };
    loadMarketInfo();
  }, [allStocks]);

  const getMarketColor = (market?: string) => {
    switch (market) {
      case '上市': return { bg: '#e3f2fd', border: '#1976d2' };
      case '上櫃': return { bg: '#fff3e0', border: '#f57c00' };
      case '興櫃': return { bg: '#e8f5e9', border: '#388e3c' };
      default: return { bg: '#f5f5f5', border: '#9e9e9e' };
    }
  };
  const [watchlistStocks, setWatchlistStocks] = useState<Set<string>>(new Set());
  const [snackbar, setSnackbar] = useState<{open: boolean, message: string, severity: 'success' | 'error'}>({open: false, message: '', severity: 'success'});
  const [groupSelectorOpen, setGroupSelectorOpen] = useState(false);
  const [selectedStockForGroup, setSelectedStockForGroup] = useState<Stock | null>(null);
  const [expandedCards, setExpandedCards] = useState<Set<string>>(new Set());

  // Use searchStocks for filtering to maintain the limit while searching
  const filteredStocks = searchTerm.trim() 
    ? searchStocks(searchTerm, stocksLimit)
    : allStocks;

  // 載入關注清單狀態
  React.useEffect(() => {
    const loadWatchlistStatus = async () => {
      if (state.currentAccountId && state.watchlist) {
        const watchedSymbols = new Set<string>();
        
        // 從未分組收集股票代號
        state.watchlist.ungroupedStockReferences?.forEach(ref => {
          watchedSymbols.add(ref.symbol);
        });
        
        // 從分組收集股票代號
        state.watchlist.groups?.forEach(group => {
          group.stockReferences?.forEach(ref => {
            watchedSymbols.add(ref.symbol);
          });
        });
        
        setWatchlistStocks(watchedSymbols);
      }
    };
    
    loadWatchlistStatus();
  }, [state.watchlist, state.currentAccountId]);

  // 處理關注清單按鈕點擊
  const handleWatchlistButtonClick = async (stock: Stock) => {
    if (!state.currentAccountId) {
      setSnackbar({open: true, message: '請先登入', severity: 'error'});
      return;
    }
    
    const isWatched = watchlistStocks.has(stock.symbol);
    
    if (isWatched) {
      // 如果已經在關注清單中，直接移除
      try {
        await WatchlistService.removeStockReference(state.currentAccountId, stock.symbol);
        setSnackbar({open: true, message: `已將 ${stock.symbol} 從關注清單移除`, severity: 'success'});
      } catch (error) {
        console.error('Remove from watchlist error:', error);
        setSnackbar({open: true, message: '移除失敗，請重試', severity: 'error'});
      }
    } else {
      // 如果不在關注清單中，打開分組選擇對話框
      setSelectedStockForGroup(stock);
      setGroupSelectorOpen(true);
    }
  };

  // 處理分組選擇確認
  const handleGroupSelection = async (groupId: string | null, newGroupName?: string) => {
    if (!selectedStockForGroup || !state.currentAccountId) {
      return;
    }

    try {
      const stockRef = {
        symbol: selectedStockForGroup.symbol,
        name: selectedStockForGroup.name,
      };

      if (newGroupName) {
        // 創建新分組並添加股票
        const colors = ['#2196f3', '#ff9800', '#4caf50', '#f44336', '#9c27b0', '#00bcd4', '#ff5722', '#795548'];
        const randomColor = colors[Math.floor(Math.random() * colors.length)];
        const groupId = await WatchlistService.createGroup(state.currentAccountId, newGroupName, randomColor);
        await WatchlistService.addStockReferenceToGroup(state.currentAccountId, groupId, stockRef);
        setSnackbar({open: true, message: `已建立分組「${newGroupName}」並加入 ${selectedStockForGroup.symbol}`, severity: 'success'});
      } else if (groupId === null) {
        // 加入到未分類
        await WatchlistService.addStockReference(state.currentAccountId, stockRef);
        setSnackbar({open: true, message: `已將 ${selectedStockForGroup.symbol} 加入未分類關注清單`, severity: 'success'});
      } else {
        // 加入到指定分組
        const targetGroup = (state.watchlist?.groups || []).find(g => g.id === groupId);
        await WatchlistService.addStockReferenceToGroup(state.currentAccountId, groupId, stockRef);
        setSnackbar({open: true, message: `已將 ${selectedStockForGroup.symbol} 加入分組「${targetGroup?.name}」`, severity: 'success'});
      }
    } catch (error) {
      console.error('Add to watchlist error:', error);
      setSnackbar({open: true, message: '加入關注清單失敗，請重試', severity: 'error'});
    }

    setSelectedStockForGroup(null);
  };

  // 處理展開/收合股利資訊
  const handleExpandToggle = (symbol: string) => {
    setExpandedCards(prev => {
      const newSet = new Set(prev);
      if (newSet.has(symbol)) {
        newSet.delete(symbol);
      } else {
        newSet.add(symbol);
      }
      return newSet;
    });
  };

  return (
    <Box sx={{ p: { xs: 1.5, sm: 2, md: 3 } }}>
      <PageHeader
        title="股票報價"
        icon={ShowChart}
        subtitle={`實時股票行情與交易 • 顯示前${stocksLimit}支，共${totalStocksCount}支股票`}
        actions={null}
      />

      {/* Search Bar */}
      <Paper sx={{ p: { xs: 1.5, sm: 2 }, mb: { xs: 2, sm: 3 } }}>
        <TextField
          fullWidth
          variant="outlined"
          placeholder="搜尋股票代號或名稱..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search />
              </InputAdornment>
            ),
            sx: { fontSize: '16px' },
          }}
          sx={{
            '& .MuiOutlinedInput-root': {
              '&:hover fieldset': {
                borderColor: '#1565c0',
              },
              '&.Mui-focused fieldset': {
                borderColor: '#1565c0',
              },
            },
          }}
        />
      </Paper>

      {/* Stock List - Card Style for Mobile */}
      <Box sx={{ 
        display: 'grid',
        gap: { xs: 1.5, sm: 2 },
        gridTemplateColumns: { 
          xs: '1fr', 
          sm: 'repeat(2, 1fr)', 
          md: 'repeat(3, 1fr)', 
          lg: 'repeat(4, 1fr)', 
          xl: 'repeat(5, 1fr)' 
        }
      }}>
        {filteredStocks.map((stock) => {
          const isExpanded = expandedCards.has(stock.symbol);
          const marketInfo = stockMarketInfo.get(stock.symbol);
          const marketColors = getMarketColor(marketInfo?.market);
          
          return (
            <Card 
              key={stock.symbol} 
              sx={{ 
                border: `2px solid ${marketColors.border}`,
                backgroundColor: marketColors.bg,
                transition: 'all 0.2s ease-in-out',
                '&:hover': {
                  boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                  transform: 'translateY(-2px)',
                },
              }}
            >
              <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
                {/* Header Row */}
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                  <Box sx={{ flex: 1 }}>
                    <Typography 
                      variant="h6" 
                      fontWeight="bold" 
                      color="primary" 
                      sx={{ fontSize: { xs: '1.1rem', sm: '1.25rem' } }}
                    >
                      {stock.symbol}
                    </Typography>
                    <Typography 
                      variant="body2" 
                      color="text.secondary"
                      sx={{ fontSize: { xs: '0.8rem', sm: '0.875rem' }, mt: 0.5 }}
                    >
                      {StockNameManager.getStockName(stock.symbol)}
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 0.5 }}>
                    <Typography 
                      variant="h5" 
                      fontWeight="bold" 
                      sx={{ 
                        fontSize: { xs: '1.3rem', sm: '1.5rem' },
                        color: '#2c3e50'
                      }}
                    >
                      ${stock.currentPrice}
                    </Typography>
                    
                    {/* Action Buttons - 在價格下方 */}
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <Tooltip title={watchlistStocks.has(stock.symbol) ? '從關注清單移除' : '加入關注清單'}>
                        <IconButton
                          size="small"
                          onClick={() => handleWatchlistButtonClick(stock)}
                          sx={{ 
                            color: watchlistStocks.has(stock.symbol) ? '#ff9800' : '#9e9e9e',
                            border: '1px solid #e0e0e0',
                            borderRadius: '50%',
                            width: 32,
                            height: 32,
                            backgroundColor: 'rgba(255, 255, 255, 0.9)',
                            '&:hover': {
                              color: '#ff9800',
                              borderColor: '#ff9800',
                              backgroundColor: 'rgba(255, 152, 0, 0.1)',
                              transform: 'scale(1.1)',
                            },
                            transition: 'all 0.2s ease-in-out'
                          }}
                        >
                          {watchlistStocks.has(stock.symbol) ? <Bookmark fontSize="small" /> : <BookmarkBorder fontSize="small" />}
                        </IconButton>
                      </Tooltip>
                      
                      <Tooltip title={isExpanded ? "收合股利資訊" : "查看股利資訊"}>
                        <IconButton
                          size="small"
                          onClick={() => handleExpandToggle(stock.symbol)}
                          sx={{ 
                            color: isExpanded ? '#1976d2' : '#9e9e9e',
                            border: '1px solid #e0e0e0',
                            borderRadius: '50%',
                            width: 32,
                            height: 32,
                            backgroundColor: 'rgba(255, 255, 255, 0.9)',
                            '&:hover': {
                              color: '#1976d2',
                              borderColor: '#1976d2',
                              backgroundColor: 'rgba(25, 118, 210, 0.1)',
                              transform: 'scale(1.1)',
                            },
                            transition: 'all 0.2s ease-in-out'
                          }}
                        >
                          <Assessment fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </Box>
                </Box>

                {/* Change Row */}
                {(() => {
                  const direction = getTrendDirection(stock.change);
                  const TrendIconComponent = direction === 'down' ? TrendingDown : TrendingUp;
                  const changeColor = getTrendTextColor(stock.change);
                  const chipBaseProps = getTrendChipProps(stock.changePercent);
                  const { sx: chipBaseSx, variant: chipBaseVariant, ...chipRest } = chipBaseProps;
                  const chipVariant = chipBaseVariant ?? (Math.abs(stock.changePercent) > 2 ? 'filled' : 'outlined');
                  return (
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <TrendIconComponent sx={{ fontSize: 20, color: changeColor }} />
                        <Typography
                          variant="body1"
                          fontWeight="bold"
                          sx={{ color: changeColor }}
                        >
                          {stock.change >= 0 ? '+' : ''}{stock.change}
                        </Typography>
                      </Box>
                      
                      <Chip
                        {...chipRest}
                        label={`${stock.changePercent >= 0 ? '+' : ''}${stock.changePercent.toFixed(2)}%`}
                        variant={chipVariant}
                        sx={{ 
                          fontWeight: 'bold', 
                          fontSize: { xs: '0.75rem', sm: '0.8125rem' },
                          height: { xs: 28, sm: 32 },
                          ...(chipBaseSx || {})
                        }}
                      />
                    </Box>
                  );
                })()}
              </CardContent>
              
              
              {/* Collapsible Dividend Display */}
              <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                <Box sx={{ px: { xs: 2, sm: 3 }, pb: { xs: 2, sm: 3 } }}>
                  <DividendDisplay
                    stockSymbol={stock.symbol}
                    stockName={StockNameManager.getStockName(stock.symbol)}
                    currentPrice={stock.currentPrice}
                    showTitle={false}
                    maxRecords={5}
                    compact={true}
                  />
                </Box>
              </Collapse>
            </Card>
          );
        })}
      </Box>

      {filteredStocks.length === 0 && searchTerm && (
        <Box sx={{ textAlign: 'center', py: 8 }}>
          <Typography variant="h6" color="text.secondary">
            沒有找到符合 "{searchTerm}" 的股票
          </Typography>
        </Box>
      )}
      
      {/* Group Selector Dialog */}
      <GroupSelectorDialog
        open={groupSelectorOpen}
        onClose={() => {
          setGroupSelectorOpen(false);
          setSelectedStockForGroup(null);
        }}
        onConfirm={handleGroupSelection}
        title="選擇關注清單分組"
        groups={state.watchlist?.groups || []}
        stockName={selectedStockForGroup?.name}
        stockSymbol={selectedStockForGroup?.symbol}
        showUngroupedOption={true}
        confirmButtonText="加入關注"
      />
      
      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar({...snackbar, open: false})}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert 
          onClose={() => setSnackbar({...snackbar, open: false})} 
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default StockList;
